// Read-only Accounts Summary rollup (planning.md §4). Sits directly below the
// Line Items Summary on every screen that shows line items: one row per distinct GL
// account chosen, with that account's summed line total, sorted descending by
// total, plus a grand-total row (which equals the line items total by
// construction — the two blocks read against each other at a glance).
//
// Cross-company splits break down by company: when a line is pinned to a
// company other than the record's, a leading Company column appears, rows are
// keyed by company + account (accounts descending within, companies ordered by
// subtotal), with a per-company subtotal above the grand total — mirroring the
// line items table's own conditional Company column.
//
// Unassigned lines group under a single "(No account)" row so the totals still
// add up and the gap stays visible. A HALF-CODED line — one that has an expense
// type and is waiting on a cost center — groups there too, but the fact that it
// is half-coded is NOT written into the table as a row (owner, 2026-08-11: "do
// not write 'Awaiting cost center' into a row within the Accounts Summary
// table"). It is stated once beneath the grand total instead, in the same
// `DotPill` shape as the Line Items Summary's "Reconciles" chip.
//
// That line carries the half-coded DOLLARS, not just a count. A count cannot say
// whether the gap is $40 of paper towels or half the invoice, and the amount is
// the part a reviewer acts on — so dropping the labelled row must not drop the
// figure with it. The pill states both.
//
// THE EDITABLE FORM'S OWN MOUNT GROUPS BY EXPENSE TYPE, not by account
// (`groupBy="expense_type"`) — neither step `EditableInvoiceForm` covers
// (Import Control or AP/AR) ever holds a cost center, so a `ChartImplied` line
// there already carries both halves and grouping by account would render
// "689109-67-520 / A&G COPIER" on a screen with no cost center to show.
// UPDATE, item 3 (2026-08-13): this component no longer mounts at Import
// Control at all — that step's screen was cut down to the Company picker,
// Comments and Save — so in practice `groupBy="expense_type"` now only ever
// fires at AP/AR Approval, which still holds no cost center either.
//
// Derived display only — never editable, never written back. One shared
// component so the surfaces can't drift; callers pass a flat row list and this
// groups/sums.
//
// Presented as a peer section of the Line Items Summary it follows — same heading
// component, same divider, same table-header styling. It used to render its own
// micro-caps heading and column labels, which read as a different design
// language from every block around it (user note 2026-08-02).
//
// The grand-total row's own line carries a green "Reconciles" `DotPill` below
// the table (moved out of the Total row's label cell, item 15, 2026-08-13)
// when `subtotal`/`tax`/`total` are supplied and pass the same `checkAmounts`
// test as the Line Items Summary footer above it (owner item 16, 2026-08-12)
// — never a red one; a mismatch just shows no pill.
import {
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Subtitle2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
// PORT NOTE: the `compact` heading variant existed for the claim detail modal,
// which titled its sections differently. Claims is gone, so this renders one
// way — as a peer section of the Line Items Summary above it.
import type { ReactNode } from 'react'
import { checkAmounts, formatCurrency } from '../lib/format'
import { TINT_PILLS } from '../lib/theme'
import { DotPill } from './Badges'

/** One line's contribution: which account (+ optional company) and how much. */
export interface AccountsSummaryRow {
  accountId?: string | null
  accountName?: string | null
  /** "What is it?" — the grouping key at Import Control. */
  expenseTypeCode?: string | null
  expenseTypeLabel?: string | null
  companyId?: string | null
  companyName?: string | null
  total: number
}

interface Props {
  rows: AccountsSummaryRow[]
  /**
   * `'expense_type'` at Import Control, where the cost center must not appear.
   * `'account'` everywhere else. Defaults to `'account'`.
   */
  groupBy?: 'account' | 'expense_type'
  /** The parent record's company id. A leading Company column + per-company
      breakdown appears only when some row's company differs from it (a genuine
      cross-company split). */
  recordCompanyId?: string | null
  /** Invoice amounts, for the green "Reconciles" pill on the Total row — the
      same DotPill on the same `checkAmounts` test as the Line Items Summary
      footer above (`InvoiceLineItemsTable`'s ReconcileFooter, owner item 16,
      2026-08-12). Omitted (or all null) = no pill, never a red one. */
  subtotal?: number | string | null
  tax?: number | string | null
  total?: number | string | null
}

const NO_ACCOUNT = '(No account)'
const NO_EXPENSE_TYPE = '(No expense type)'

/** A line with an expense type and no cost center: coded, just not finished.
    Reported beneath the total rather than as a row of its own. */
function isAwaitingCostCenter(r: AccountsSummaryRow): boolean {
  return !r.accountId?.trim() && !r.accountName?.trim() && !!r.expenseTypeCode?.trim()
}

const useStyles = makeStyles({
  // Peer section on the invoice surfaces: rule, heading, table — the same
  // rhythm (and the same 16px gaps) as the Line Items Summary above it.
  wrap: { display: 'flex', flexDirection: 'column', gap: '16px' },
  table: { tableLayout: 'fixed', width: '100%', fontVariantNumeric: 'tabular-nums' },
  num: { textAlign: 'right' },
  numHead: {
    textAlign: 'right',
    '& .fui-TableHeaderCell__button': { justifyContent: 'flex-end', width: '100%' },
  },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  subtotalRow: { color: tokens.colorNeutralForeground2 },
  subtotalLabel: { fontStyle: 'italic' },
  grandRow: { fontWeight: tokens.fontWeightSemibold },
  // `wrap` is a flex COLUMN, so its default `align-items: stretch` would draw an
  // inline-flex pill as a full-width band. Right-align it under the Total column
  // the way the Line Items Summary's own chip sits under its total. One class
  // for both pill rows below the table — the Reconciles pill's own row (item
  // 15, 2026-08-13, kept out of the Total row's amount cell because that cell
  // is `width: 120` with tabular-nums and a pill inside it breaks the
  // right-edge alignment with the account rows above it) and the awaiting-a-
  // cost-center pill's row are byte-identical rules, so they share one class
  // rather than two. `marginTop: '-8px'` because `wrap`'s flex-column
  // `gap: 16px` is more air than two pills stacked under one table need.
  pillLine: { display: 'flex', justifyContent: 'flex-end', marginTop: '-8px' },
})

function accountLabel(r: AccountsSummaryRow): string {
  const name = r.accountName?.trim()
  if (name) return name
  const id = r.accountId?.trim()
  if (id) return id
  return NO_ACCOUNT
}

function expenseTypeLabel(r: AccountsSummaryRow): string {
  const label = r.expenseTypeLabel?.trim()
  if (label) return label
  return r.expenseTypeCode?.trim() || NO_EXPENSE_TYPE
}

/** { key, label, total } grouped by account, sorted descending by total. Keyed
    by account id, else name — a flow-written row can carry a name with no id,
    and must get its own correctly-labelled row (two id-less named accounts stay
    distinct) rather than joining the "(No account)" bucket. Only rows with
    neither id nor name land there. */
function byAccount(
  rows: AccountsSummaryRow[],
  groupBy: 'account' | 'expense_type',
): { key: string; label: string; total: number }[] {
  const map = new Map<string, { key: string; label: string; total: number }>()
  for (const r of rows) {
    const key =
      groupBy === 'expense_type'
        ? r.expenseTypeCode?.trim() || NO_EXPENSE_TYPE
        : r.accountId?.trim() || r.accountName?.trim() || NO_ACCOUNT
    const existing = map.get(key)
    if (existing) existing.total += r.total
    else {
      map.set(key, {
        key,
        label: groupBy === 'expense_type' ? expenseTypeLabel(r) : accountLabel(r),
        total: r.total,
      })
    }
  }
  return [...map.values()].sort((a, b) => b.total - a.total)
}

export function AccountsSummary({ rows, recordCompanyId, groupBy = 'account', subtotal, tax, total }: Props) {
  const s = useStyles()
  if (!rows || rows.length === 0) return null

  const showCompany = rows.some((r) => r.companyId && r.companyId !== (recordCompanyId ?? undefined))
  const grand = rows.reduce((sum, r) => sum + r.total, 0)
  // Green only. A red "Doesn't reconcile" here would restate, six inches lower,
  // the red pill the Line Items footer already shows. With no printed amounts
  // there is nothing to reconcile against and nothing renders.
  const amountsKnown = subtotal != null || tax != null || total != null
  const reconciles = amountsKnown && checkAmounts(grand, subtotal, tax, total).reconciles

  // Gated to the account grouping: AP/AR Approval (the only step that still
  // mounts this component with `groupBy="expense_type"` — it no longer mounts
  // at Import Control at all, owner item 3, 2026-08-13) never populates an
  // account either, so ungated this would claim every line on a screen that
  // has no cost center to report.
  const awaiting = groupBy === 'account' ? rows.filter(isAwaitingCostCenter) : []
  const awaitingTotal = awaiting.reduce((sum, r) => sum + r.total, 0)

  return (
    <div className={s.wrap}>
      <Divider />
      <Subtitle2>Accounts Summary</Subtitle2>
      <Table size="small" aria-label="Accounts summary" className={s.table}>
        <TableHeader>
          <TableRow>
            {showCompany && <TableHeaderCell>Company</TableHeaderCell>}
            <TableHeaderCell>{groupBy === 'expense_type' ? 'Expense type' : 'Account'}</TableHeaderCell>
            <TableHeaderCell style={{ width: 120 }} className={s.numHead}>Total</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {showCompany ? <CompanyGroups rows={rows} groupBy={groupBy} /> : (
            byAccount(rows, groupBy).map((a) => (
              <TableRow key={a.key}>
                <TableCell className={a.label === NO_ACCOUNT || a.label === NO_EXPENSE_TYPE ? s.none : undefined}>{a.label}</TableCell>
                <TableCell className={s.num}>{formatCurrency(a.total)}</TableCell>
              </TableRow>
            ))
          )}
          <TableRow className={s.grandRow}>
            {showCompany && <TableCell />}
            <TableCell>Total</TableCell>
            <TableCell className={s.num}>{formatCurrency(grand)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {/* Reconciles pill, in its own right-aligned row below the table rather
          than in the Total row's label cell (item 15, 2026-08-13) — see
          `pillLine`'s comment above for why. */}
      {reconciles && (
        <div className={s.pillLine}>
          <DotPill pill={TINT_PILLS.green}>Reconciles</DotPill>
        </div>
      )}
      {awaiting.length > 0 && (
        <div className={s.pillLine}>
          <DotPill pill={TINT_PILLS.yellow}>
            {formatCurrency(awaitingTotal)} awaiting a cost center
            {awaiting.length === 1 ? ' (1 line)' : ` (${awaiting.length} lines)`}
          </DotPill>
        </div>
      )}
    </div>
  )
}

/** Cross-company breakdown: company groups ordered by subtotal (desc), accounts
    descending within each, and a per-company subtotal row above the grand total. */
function CompanyGroups({
  rows,
  groupBy,
}: {
  rows: AccountsSummaryRow[]
  groupBy: 'account' | 'expense_type'
}) {
  const s = useStyles()
  const map = new Map<string, { label: string; rows: AccountsSummaryRow[]; total: number }>()
  for (const r of rows) {
    const key = r.companyId?.trim() || ''
    const label = r.companyName?.trim() || '(No company)'
    const g = map.get(key)
    if (g) { g.rows.push(r); g.total += r.total }
    else map.set(key, { label, rows: [r], total: r.total })
  }
  const groups = [...map.entries()].sort((a, b) => b[1].total - a[1].total)
  const out: ReactNode[] = []
  for (const [key, g] of groups) {
    const accounts = byAccount(g.rows, groupBy)
    accounts.forEach((a, i) => {
      out.push(
        <TableRow key={`${key}-${a.key}`}>
          <TableCell>{i === 0 ? g.label : ''}</TableCell>
          <TableCell className={a.label === NO_ACCOUNT || a.label === NO_EXPENSE_TYPE ? s.none : undefined}>{a.label}</TableCell>
          <TableCell className={s.num}>{formatCurrency(a.total)}</TableCell>
        </TableRow>,
      )
    })
    out.push(
      <TableRow key={`${key}-subtotal`} className={s.subtotalRow}>
        <TableCell />
        <TableCell className={s.subtotalLabel}>{g.label} subtotal</TableCell>
        <TableCell className={s.num}>{formatCurrency(g.total)}</TableCell>
      </TableRow>,
    )
  }
  return <>{out}</>
}
